import { apiClient } from "@/services/api"
import { logger } from "@/services/logger"
import { defineStore } from "pinia"

const authHeaders = { headers: { tenant: true, authorization: true } }

// Milliseconds in a day, for grace-period math.
const DAY_MS = 1000 * 60 * 60 * 24

export const useSubscriptionStore = defineStore("subscription", {
  state: () => ({
    // Raw backend subscription status enum: NONE | INCOMPLETE | ACTIVE | PAST_DUE | CANCELED | UNPAID
    status: null,
    planTier: null,
    addonFeatureSets: [],
    seats: { purchased: 0, used: 0 },
    // Billing cadence of the current subscription: MONTH | YEAR
    interval: "MONTH",
    // Effective feature-sets = plan tier bundle ∪ add-ons (or ALL if billing-exempt)
    entitlements: [],
    gracePeriodEnd: null,
    currentPeriodEnd: null,
    cancelAtPeriodEnd: false,
    billingExempt: false,
    // Catalog + Stripe config (loaded lazily before checkout)
    catalog: null,
    publishableKey: null,
    // Active PaymentIntent client secret for the Payment Element
    clientSecret: null,
    // Plan/seat selection handed off from the standalone Plans page to Checkout (subscribe) or the
    // settings change-plan flow. Shape: { planTier, addonFeatureSets, seats, interval }. Consumed once,
    // then cleared.
    pendingSelection: null,
    // Registration form values, preserved when the visitor jumps to the Plans page ("Change" link) and
    // back so already-entered input is restored. Shape mirrors Registration's `state` (no workspace:
    // signup does not ask for one — the id is derived from the company name). Cleared on submit.
    registrationDraft: null,
    // Subscription billing history (past invoices), loaded lazily for the billing settings list.
    invoices: [],
    // True once loadStatus has resolved at least once — guards must not act before this.
    loaded: false,
    // Who may act on the subscription, from GET /billing/access (the one billing route readable without
    // VIEW_BILLING). Null until loaded — the checkout page must not decide anything before it is known,
    // since both "may pay" and "may not" render a different page.
    access: null,
  }),
  getters: {
    // Access is granted while active, within grace, or billing-exempt (grandfathered).
    hasAccess: state => {
      if (state.billingExempt) return true
      if (state.status === "ACTIVE") return true
      return !!(state.gracePeriodEnd && new Date(state.gracePeriodEnd).getTime() > Date.now())
    },
    isLocked() {
      return this.loaded && !this.hasAccess
    },
    isExempt: state => state.billingExempt,
    // True when the backend runs the stub billing gateway (no real Stripe): its publishable key is a
    // "…placeholder" value. Lets Checkout render a mock payment step instead of a live Stripe Element,
    // so the flow is viewable/testable without configuring Stripe.
    mockBilling: state => !!state.publishableKey && state.publishableKey.includes("placeholder"),
    inGrace() {
      return (
        !this.billingExempt &&
        this.status !== "ACTIVE" &&
        !!this.gracePeriodEnd &&
        new Date(this.gracePeriodEnd).getTime() > Date.now()
      )
    },
    graceDaysLeft: state => {
      if (!state.gracePeriodEnd) return 0
      const diff = new Date(state.gracePeriodEnd).getTime() - Date.now()
      return diff > 0 ? Math.ceil(diff / DAY_MS) : 0
    },
    hasEntitlement: state => featureSet => state.entitlements.includes(featureSet),
    // Whether the signed-in user may subscribe/change/cancel. Defaults to false while `access` is still
    // null: the paywall would rather show the "ask someone else" page for a moment than flash a checkout
    // at a user whose click is about to 403.
    canManageBilling: state => state.access?.canManageBilling === true,
    // Colleagues holding MANAGE_BILLING, for the "please contact" line. Empty is a valid answer.
    billingContacts: state => state.access?.contacts ?? [],
  },
  actions: {
    setStatus(data) {
      this.status = data.status
      this.planTier = data.planTier
      this.addonFeatureSets = data.addonFeatureSets ?? []
      this.seats = { purchased: data.seats ?? 0, used: data.usedSeats ?? 0 }
      this.interval = data.interval ?? "MONTH"
      this.entitlements = data.entitlements ?? []
      this.gracePeriodEnd = data.gracePeriodEnd
      this.currentPeriodEnd = data.currentPeriodEnd
      this.cancelAtPeriodEnd = data.cancelAtPeriodEnd ?? false
      this.billingExempt = data.billingExempt ?? false
    },
    loadStatus() {
      return apiClient
        .get("/billing/subscription", authHeaders)
        .then(res => {
          if (res.status === 200) {
            this.setStatus(res.data)
            this.loaded = true
          }
          return res
        })
        .catch(error => {
          // A failed status load (missing VIEW_BILLING permission, transient/network error, etc.)
          // must never block app boot or wrongly lock a tenant out. Leave `loaded` false so the
          // subscription guard stays inert; the backend's 402 gate remains the single source of
          // truth for an actual lockout. Swallow the error so callers awaiting this never reject.
          logger.error(`Failed to load subscription status: ${error}`)
          return null
        })
    },
    // Who may act on the subscription, plus whom to ask if the caller may not. Unlike every other
    // /billing route this needs no VIEW_BILLING, so it answers for the users the paywall strands.
    loadAccess() {
      return apiClient
        .get("/billing/access", authHeaders)
        .then(res => {
          if (res.status === 200) this.access = res.data
          return this.access
        })
        .catch(error => {
          // Leave `access` null so the UI keeps treating the answer as unknown. It renders the
          // no-permission page in that case, which is the safe side: the alternative is offering a
          // checkout whose first click 403s.
          logger.error(`Failed to load billing access: ${error}`)
          return null
        })
    },
    loadCatalog() {
      return apiClient
        .get("/billing/catalog", authHeaders)
        .then(res => {
          if (res.status === 200) {
            this.catalog = res.data
            this.publishableKey = res.data.publishableKey
          }
          return res
        })
        .catch(error => {
          // The catalog can 403 before billing permissions are granted / the tenant is entitled.
          // Swallow it so a caller's Promise.all never rejects into an unhandled promise rejection;
          // the UI degrades gracefully to an empty catalog (plans/addons default to []).
          logger.error(`Failed to load billing catalog: ${error}`)
          return null
        })
    },
    // Public catalog for the unauthenticated marketing plans page. Sends no tenant/auth headers so it
    // works for anonymous visitors (and logged-in users without VIEW_BILLING) against /public/billing.
    loadPublicCatalog() {
      return apiClient
        .get("/public/billing/catalog", { headers: { tenant: false, authorization: false } })
        .then(res => {
          if (res.status === 200) {
            this.catalog = res.data
            this.publishableKey = res.data.publishableKey
          }
          return res
        })
        .catch(error => {
          logger.error(`Failed to load public billing catalog: ${error}`)
          return null
        })
    },
    subscribe({ planTier, addonFeatureSets, seats, interval }) {
      return apiClient
        .post("/billing/subscribe", { planTier, addonFeatureSets, seats, interval }, authHeaders)
        .then(res => {
          this.clientSecret = res.data.clientSecret
          this.publishableKey = res.data.publishableKey
          this.status = res.data.status
          return res.data
        })
    },
    // `interval` may be omitted — the backend then keeps the tenant's current cadence.
    changeSubscription({ planTier, addonFeatureSets, seats, interval }) {
      return apiClient
        .post("/billing/subscription", { planTier, addonFeatureSets, seats, interval }, authHeaders)
        .then(res => {
          if (res.status === 200) {
            this.setStatus(res.data)
          }
          return res.data
        })
    },
    cancel({ atPeriodEnd = true } = {}) {
      return apiClient.post("/billing/subscription/cancel", { atPeriodEnd }, authHeaders).then(res => {
        if (res.status === 200) {
          this.setStatus(res.data)
        }
        return res.data
      })
    },
    createSetupIntent() {
      return apiClient.post("/billing/payment-method/setup-intent", {}, authHeaders).then(res => {
        this.publishableKey = res.data.publishableKey
        return res.data
      })
    },
    // Subscription billing history for the settings invoice list. Degrades to an empty list on error
    // (e.g. no billing customer yet) so the UI can render an empty state instead of failing.
    loadInvoices() {
      return apiClient
        .get("/billing/invoices", authHeaders)
        .then(res => {
          this.invoices = res.data.invoices ?? []
          return this.invoices
        })
        .catch(error => {
          logger.error(`Failed to load invoices: ${error}`)
          this.invoices = []
          return []
        })
    },
    // Poll status after a confirmed payment until access is granted (the webhook may lag).
    async refreshAfterPayment(attempts = 6, delayMs = 1500) {
      for (let i = 0; i < attempts; i++) {
        await this.loadStatus()
        if (this.hasAccess) return true
        await new Promise(resolve => setTimeout(resolve, delayMs))
      }
      return this.hasAccess
    },
  },
})
